import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import { Button } from "../../../../components/src/Button";
import { gql, useMutation, useQuery } from "urql";
import { Alert, AlertVariant } from "../../../../components/src/Alert";
import stableStringify from "json-stable-stringify";
import { useQueryState } from "../../../../components/src/use-query-state";

import { AnnualReportComponent } from "./AnnualReportComponent";
import { AnnualReportEditor } from "./AnnualReportEditor";
import { CreateReportInput, ReportStatusEnum } from "../../types.generated";
import { formikUrqlErrorFormater } from "../../../../components/src/common/formik-urql-error-helper";
import { generateRandomString } from "../../../../components/src/common/Utils";

const GET_REPORT = gql`
    query report($_id: GraphQLObjectId!) {
        report(_id: $_id) {
            _id
            name
            status
            clientId
            clientName
            date
            type
            updateUserId
            updateUserInfo {
                name
            }
            data
        }
    }
`;

export const UPDATE_REPORT = gql`
    mutation updateReport($input: UpdateReportInput!) {
        report: updateReport(input: $input) {
            _id
            name
            status
            clientId
            clientName
            date
            type
            updateUserId
            updateUserInfo {
                name
            }
            data
        }
    }
`;

export const CREATE_REPORT = gql`
    mutation createReport($input: CreateReportInput!) {
        report: createReport(input: $input) {
            _id
            name
            status
            clientId
            clientName
            date
            type
            updateUserId
            updateUserInfo {
                name
            }
            data
        }
    }
`;

export const AnnualReportPage = (): React.ReactElement => {
    const { id } = useParams<{ id: string }>();
    const [, navigate] = useLocation();
    const [edit, setEdit] = useQueryState("edit", "false");
    const [report, setReport] = useState(null);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const [{ fetching, error, data }] = useQuery({
        query: GET_REPORT,
        variables: { _id: id },
        requestPolicy: "cache-and-network"
    });

    const [_, createReport] = useMutation(CREATE_REPORT);

    useEffect(() => {
        if (!report && data && data.report) {
            setReport(data.report);
        } else if (data && stableStringify(data.report) !== stableStringify(report)) {
            setReport(data.report);
        }
    }, [data, id, report]);

    useEffect(() => {
        if (report) {
            document.title = `Annual/semiannual Report - ${report.clientName}`;
        }
    }, [report]);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>Error: {JSON.stringify(error, null, 2)}</p>;

    return (
        <div>
            <div className="print-none print:hidden">
                <Link to={"/reporting/annualreports"}>Annual reports</Link>
            </div>

            <div className="mt-2 print-none print:hidden">
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => {
                        if (edit === "true") {
                            setEdit("false");
                        } else {
                            setEdit("true");
                        }
                    }}
                >
                    {edit === "false" ? "Edit" : "Stop editing"}
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="ml-2"
                    onClick={async () => {
                        const input: CreateReportInput = {
                            clientId: report.clientId,
                            data: report.data,
                            date: report.date,
                            name: report.name + "_" + generateRandomString(10),
                            status: ReportStatusEnum.Active,
                            type: "AnnualReport"
                        };

                        await createReport({ input })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    const message = formikUrqlErrorFormater(result.error);
                                    setAlert({ color: "danger", visible: true, message });
                                } else {
                                    setReport(result.data.report);
                                    navigate("/reporting/annualreports/" + result.data.report._id, { replace: false });
                                }
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }}
                >
                    Clone
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="ml-2"
                    title="Print"
                    onClick={() => {
                        const title = document.title;
                        document.title = "annual-report-" + report.date + "-" + report.clientName.replace(/ /g, "-").toLowerCase();
                        window.print();
                        document.title = title;
                    }}
                >
                    Print
                </Button>
            </div>

            <div className="flex flex-wrap -mx-2 print:hidden">
                {alert.visible ? (
                    <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                        {alert.message}
                    </Alert>
                ) : null}
            </div>

            {report && report.status === ReportStatusEnum.Locked ? (
                <div className="print-none print:hidden">
                    <i style={{ color: "red" }}>{"This report has status Locked, not possible to edit anymore."}</i>
                </div>
            ) : null}
            {report ? (
                <div className="mt-2">
                    {edit === "true" ? <AnnualReportEditor report={report} /> : <AnnualReportComponent report={report} />}
                </div>
            ) : null}
        </div>
    );
};
